import {useEffect, useState} from "react";
import {subscribe} from "./PusherClient";
import {useGetMainBranch} from "../../store/api/AuthSlice";
import {arrivedFromV2, forgetArrival, isHandoffToV2Enabled, redirectToDispatchV2} from "../../utils/BranchHandoff";

/**
 * Hands the tab over to the automated dispatch app (v2) when the selected branch
 * has `isAutomationEnabled`.
 *
 * The flag comes from the branch list (initial value and every branch switch)
 * and from the `organization.{iikoId}` channel (flipped while the board is
 * open). Only a confirmed `true` redirects; an unknown flag keeps the user here.
 * Nothing is blocked if the handoff cannot happen — this app serves every
 * branch regardless.
 */
const useAutomationWebsocket = (): void => {

    const mainBranch = useGetMainBranch()
    const branchId = mainBranch?.iikoId ?? ""
    const [live, setLive] = useState<{ branchId: string, enabled: boolean } | null>(null)
    const enabled = isHandoffToV2Enabled()

    useEffect(() => {
        // No subscription while the gate is closed: nothing would act on it.
        if (!branchId || !enabled) return

        const prefix = process.env.REACT_APP_PUSHER_ORGANIZATION_CHANNEL_PREFIX || "organization"
        const event = process.env.REACT_APP_PUSHER_ORGANIZATION_EVENT_NAME
            || "OrganizationEnableAutoAssignmentEvent"

        return subscribe(`${prefix}.${branchId}`, event, (data: any) => {
            // The payload arrives either bare or wrapped in a `data` key.
            const frame = data && typeof data.isAutomationEnabled === "boolean" ? data : data?.data
            if (!frame || typeof frame.isAutomationEnabled !== "boolean") return
            // Guards against a stale binding left over from a branch switch.
            if (frame.iikoId !== branchId) return

            setLive({branchId, enabled: frame.isAutomationEnabled})
        })
    }, [branchId, enabled]);

    // The socket value is the newer of the two sources and wins.
    const known = mainBranch?.isAutomationEnabled
    const confirmed = live?.branchId === branchId ? live.enabled : known
    const automated = confirmed ?? false

    useEffect(() => {
        if (!branchId || !enabled) return

        if (!automated) {
            // Clear the loop guard only on a confirmed false, not the loading default.
            if (confirmed === false && arrivedFromV2(branchId)) forgetArrival()
            return
        }

        // v2 sent us here for this branch: one side holds a stale flag.
        if (arrivedFromV2(branchId)) {
            console.warn(`Automation handoff loop for branch ${branchId}`)
            return
        }

        if (!redirectToDispatchV2(branchId)) {
            console.warn("REACT_APP_DISPATCH_V2_URL is not configured")
        }
    }, [branchId, automated, confirmed, enabled]);
}

export default useAutomationWebsocket
